import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ExternalLink } from "lucide-react";
import { BRANDS } from "@/lib/brands";
import { Reveal } from "@/components/site/Reveal";
import useSEO from "@/lib/useSEO";

export default function Brands() {
  useSEO({
    title: "Our Brands — EquiOne · Hopsy · AviPure · NutriMax | RSA Overseas",
    description:
      "Four houses under RSA Overseas: EquiOne (equine nutrition), Hopsy (small pets & rabbits), AviPure (bird nutrition) and NutriMax (human-grade oats & dry fruits).",
    path: "/brands",
  });
  return (
    <div data-testid="brands-page" className="bg-parchment">
      {/* HERO */}
      <section className="relative bg-aubergine text-parchment overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0 opacity-90"
          style={{
            backgroundImage:
              "radial-gradient(1200px 600px at 85% 20%, rgba(225,29,96,0.24), transparent 60%), radial-gradient(900px 500px at 10% 100%, rgba(147,51,234,0.18), transparent 60%)",
          }}
        />
        <div aria-hidden className="absolute inset-0 grain-overlay" />
        <div className="relative mx-auto max-w-[1440px] px-6 md:px-12 lg:px-16 pt-20 md:pt-28 pb-24 md:pb-32">
          <Reveal>
            <div className="text-[10px] uppercase tracking-[0.32em] font-semibold text-magenta flex items-center gap-4 mb-10">
              <span className="h-px w-8 bg-magenta/60" />
              The House of RSA
            </div>
            <h1
              data-testid="brands-heading"
              className="font-display font-light text-[52px] sm:text-7xl md:text-8xl lg:text-[128px] tracking-[-0.045em] leading-[0.9] text-parchment text-balance max-w-6xl"
            >
              Four{" "}
              <span className="italic text-magenta">specialties.</span> One
              standard.
            </h1>
            <p className="mt-10 max-w-2xl text-lg text-parchment/70 leading-relaxed">
              Each brand under the RSA Overseas family is formulated for the
              unique biology of its species — from sport horses and aviary
              birds to backyard rabbits and human-grade pantry staples.
            </p>
          </Reveal>
        </div>
      </section>

      {/* BRAND ROWS */}
      <section className="bg-parchment">
        <div className="mx-auto max-w-[1440px] px-6 md:px-12 lg:px-16 py-16 md:py-24">
          <div className="space-y-16 md:space-y-24">
            {BRANDS.map((b, idx) => (
              <BrandRow key={b.slug} brand={b} idx={idx} />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

function BrandRow({ brand, idx }) {
  const isRight = idx % 2 === 1;
  // Vary shapes per row for editorial rhythm
  const shapes = [
    { borderTopLeftRadius: "50% 30%", borderTopRightRadius: "50% 30%" },
    { borderRadius: "999px" },
    { borderBottomLeftRadius: "50% 30%", borderBottomRightRadius: "50% 30%" },
    { borderTopRightRadius: "50% 60%", borderBottomLeftRadius: "50% 60%" },
  ];
  const shape = shapes[idx % shapes.length];

  return (
    <article
      data-testid={`brand-row-${brand.slug}`}
      className="group grid lg:grid-cols-12 gap-x-14 gap-y-10 items-center pb-16 md:pb-24 border-b border-aubergine/15 last:border-b-0"
    >
      <Reveal
        className={`lg:col-span-5 ${isRight ? "lg:order-2" : ""}`}
        y={40}
      >
        <Link
          to={`/brands/${brand.slug}`}
          className="relative block overflow-hidden aspect-[4/5] bg-parchment ring-1 ring-aubergine/10"
          style={shape}
        >
          <motion.img
            src={brand.image}
            alt={brand.name}
            className="absolute inset-0 h-full w-full object-cover opacity-0 group-hover:opacity-30 transition-opacity duration-700"
            draggable={false}
          />
          <div className="relative h-full grid place-items-center p-10">
            {brand.logo ? (
              <motion.img
                src={brand.logo}
                alt={`${brand.name} logo`}
                className="max-h-[70%] max-w-[80%] object-contain"
                style={{ mixBlendMode: "multiply" }}
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                draggable={false}
              />
            ) : (
              <div className="text-center">
                <div className="font-display italic font-light text-6xl md:text-7xl tracking-[-0.04em] bg-gradient-to-br from-magenta via-magenta-deep to-aubergine-light bg-clip-text text-transparent">
                  {brand.name}
                </div>
                <div className="mt-4 text-[10px] tracking-[0.32em] uppercase font-semibold text-aubergine/60">
                  Premium Pantry · Globally Sourced
                </div>
              </div>
            )}
          </div>
        </Link>
      </Reveal>

      <Reveal
        className={`lg:col-span-7 ${
          isRight ? "lg:order-1 lg:pr-14" : "lg:pl-14"
        }`}
        delay={0.1}
        y={40}
      >
        <div className="text-[10px] uppercase tracking-[0.32em] font-semibold text-magenta flex items-center gap-4">
          <span className="h-px w-6 bg-magenta" />
          {brand.species}
        </div>
        <h2 className="font-display font-light mt-6 text-6xl md:text-7xl lg:text-8xl tracking-[-0.04em] leading-[0.9] text-aubergine">
          {brand.name}
        </h2>
        <div className="mt-4 font-display italic text-2xl md:text-3xl text-magenta">
          {brand.tagline}
        </div>
        <p className="mt-8 text-lg text-ink/75 leading-[1.65] max-w-xl">
          {brand.description}
        </p>
        <ul className="mt-8 divide-y divide-aubergine/15 border-y border-aubergine/15 max-w-xl">
          {brand.benefits.map((p, i) => (
            <li
              key={p}
              className="flex items-baseline gap-5 py-3.5 text-aubergine"
            >
              <span className="font-display italic font-light text-lg text-magenta w-8">
                {String(i + 1).padStart(2, "0")}
              </span>
              <span className="text-sm">{p}</span>
            </li>
          ))}
        </ul>
        <div className="mt-10 flex flex-wrap items-center gap-6">
          <Link
            to={`/brands/${brand.slug}`}
            data-testid={`brand-row-cta-${brand.slug}`}
            className="inline-flex items-center gap-3 bg-aubergine hover:bg-magenta text-parchment text-[11px] tracking-[0.28em] uppercase font-semibold px-8 py-4 transition-colors duration-300"
          >
            Discover {brand.name}
            <span className="h-px w-6 bg-parchment" />
          </Link>
          {brand.website && (
            <a
              href={brand.website}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-aubergine hover:text-magenta transition-colors text-[11px] tracking-[0.28em] uppercase font-semibold border-b border-aubergine/40 hover:border-magenta pb-1"
            >
              Visit {brand.website.replace(/^https?:\/\//, "")}
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          )}
        </div>
      </Reveal>
    </article>
  );
}
